import { getComponentLabel } from '../../app/terminology/components';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Banner, BlockStack, Button, Checkbox, InlineStack, Select, Text, TextField } from '@shopify/polaris';
import catalogJson from './blockAppearanceCatalog.json';
import agent from '../../app/api/agent';
import type { StorefrontUiSettings, ThemeAppearance, ThemeAppearanceProfile } from '../../app/models/StorefrontResultsSettings';

type Value = string | number | boolean;
interface Field { id:string; label:string; type:string; default:Value; uiKey?:string; min?:number; max?:number; step?:number; options?:{label:string;value:string}[]; }
export const blockAppearanceCatalog = (catalogJson as {kind:string;label:string;fields:Field[]}[])
    .map(component => ({ ...component, label: getComponentLabel(component.kind) }));
export const emptyThemeAppearance = (): ThemeAppearance => ({enabled:false,themeIds:[],defaults:{},profiles:[]});
export function resolveBlockAppearance(ui:StorefrontUiSettings, kind:string, profile?:ThemeAppearanceProfile) {
    const component = blockAppearanceCatalog.find(c=>c.kind===kind);
    const tokens = ui as unknown as Record<string,Value>;
    return Object.fromEntries((component?.fields || []).map(f=>{
        let inherited = f.uiKey ? tokens[f.uiKey] ?? f.default : f.default;
        if(f.type==='select') inherited=String(inherited);
        return [f.id, profile?.values[f.id] ?? ui.themeAppearance?.defaults[kind]?.[f.id] ?? inherited];
    }));
}
export function previewBlockAppearance(ui:StorefrontUiSettings, target:string): StorefrontUiSettings {
    const config = ui.themeAppearance || emptyThemeAppearance();
    const profile=config.profiles.find(p=>`${p.themeId}:${p.blockId}`===target);
    const kind=profile?.kind || target.replace('defaults:','');
    const component=blockAppearanceCatalog.find(c=>c.kind===kind);
    if(!component) return ui;
    const values=resolveBlockAppearance(ui,kind,profile), result={...ui} as unknown as Record<string,unknown>;
    component.fields.forEach(f=>{
        // Several directional controls inherit one shared token. Only the
        // primary field maps back; the preview receives all four separately.
        if(f.uiKey && !['gv-selected-gradient-end','gv-padding-right','gv-padding-bottom','gv-padding-left'].includes(f.id))
            result[f.uiKey]=typeof (ui as unknown as Record<string,unknown>)[f.uiKey]==='number'?Number(values[f.id]):values[f.id];
    });
    result.themeBlockPreview={kind,values};
    return result as unknown as StorefrontUiSettings;
}
export default function ThemeAppearanceEditor({ui,onChange,target,onTargetChange,vehicleBarInTheme=false}:{ui:StorefrontUiSettings;onChange:(changes:Partial<StorefrontUiSettings>)=>void;target:string;onTargetChange:(target:string)=>void;vehicleBarInTheme?:boolean}) {
    const [busy,setBusy]=useState(true), [error,setError]=useState(''), [notice,setNotice]=useState('');
    const latest = useRef({ui, onChange});
    latest.current = {ui, onChange};
    const mounted = useRef(false), generation = useRef(0), overridesEdited = useRef(false);
    const pending = useRef<ReturnType<typeof agent.Settings.themeAppearanceAudit> | null>(null);
    const config=ui.themeAppearance || emptyThemeAppearance();
    const profile=config.profiles.find(p=>`${p.themeId}:${p.blockId}`===target);
    const kind=profile?.kind || target.replace('defaults:','');
    const component=blockAppearanceCatalog.find(c=>c.kind===kind);
    const effective=resolveBlockAppearance(ui,kind,profile);
    const overrides=profile?.values || config.defaults[kind] || {};
    const editableComponent=component && !(vehicleBarInTheme && kind==='vehicle_search_bar');
    useEffect(()=>{
        if(vehicleBarInTheme && kind==='vehicle_search_bar') onTargetChange('shared');
    },[vehicleBarInTheme,kind,onTargetChange]);
    const setValue=(id:string,value:Value|undefined)=>{
        const values={...overrides}; if(value===undefined) delete values[id]; else values[id]=value;
        onChange({themeAppearance:profile
            ? {...config,profiles:config.profiles.map(p=>p===profile?{...p,values}:p)}
            : {...config,defaults:{...config.defaults,[kind]:values}}});
    };
    const importTheme=useCallback(async()=>{
        const requestId = ++generation.current;
        setBusy(true);setError('');setNotice('');
        try {
            // Reuse the request during React Strict Mode's effect replay.
            const audit=await (pending.current ||= agent.Settings.themeAppearanceAudit());
            if (!mounted.current || requestId !== generation.current) return;
            // The editor stays usable during the request. Merge into the latest
            // draft, never the snapshot from when the network request started.
            const currentUi = latest.current.ui;
            const current = currentUi.themeAppearance || emptyThemeAppearance();
            const added=audit.profiles.filter(p=>!current.profiles.some(old=>old.themeId===p.themeId&&old.blockId===p.blockId)).map(p=>{
                const inherited=resolveBlockAppearance(currentUi,p.kind);
                return {...p,values:Object.fromEntries(Object.entries(p.values).filter(([key,value])=>String(value).toLowerCase()!==String(inherited[key]).toLowerCase()))};
            });
            const addTheme = audit.profiles.length > 0 && !current.themeIds.includes(audit.themeId);
            if (added.length || addTheme) {
                const firstImport = current.profiles.length === 0 && current.themeIds.length === 0;
                latest.current.onChange({themeAppearance:{
                    ...current,
                    enabled: current.enabled || (firstImport && !overridesEdited.current),
                    themeIds:Array.from(new Set([...current.themeIds,audit.themeId])),
                    profiles:[...current.profiles,...added]
                }});
                setNotice(`${audit.themeName}: ${added.length} block styles added to this draft. Save to publish. Existing Appearance edits were preserved.`);
            } else {
                setNotice(audit.profiles.length
                    ? `${audit.themeName}: theme styles checked. Your existing Appearance settings are unchanged.`
                    : `${audit.themeName}: no PartFits block styles found. Your Appearance settings are unchanged.`);
            }
        } catch(e:any) {
            if (mounted.current && requestId === generation.current)
                setError(e?.response?.data?.message || e.message || 'Unable to read theme settings.');
        } finally {
            if (requestId === generation.current) {
                pending.current=null;
                if (mounted.current) setBusy(false);
            }
        }
    },[]);
    useEffect(()=>{
        mounted.current=true;
        void importTheme();
        return ()=>{mounted.current=false;generation.current++;};
    },[importTheme]);
    return <BlockStack gap="300">
        <Text as="h3" variant="headingMd">Storefront appearance</Text>
        <Text as="p" tone="subdued">{vehicleBarInTheme ? 'Customize product results and additional widget colors here. Your vehicle bar uses its theme editor settings on every page.' : 'Manage all PartFits product results and hosted results page styles here. Theme-editor settings cannot override them. Search bars on other pages and fitment widgets can use their own theme-block styles.'}</Text>
        {error&&<Banner tone="warning">Theme styles could not be loaded. You can keep editing your current settings. {error}</Banner>}
        {notice&&<Banner tone="info">{notice}</Banner>}
        {busy&&<div role="status"><Text as="p" tone="subdued">Loading published theme styles… You can keep editing.</Text></div>}
        <Button loading={busy} onClick={()=>{void importTheme();}}>{error?'Retry theme import':'Refresh published theme styles'}</Button>
        <Checkbox label="Use imported block overrides" checked={config.enabled} onChange={enabled=>{overridesEdited.current=true;onChange({themeAppearance:{...config,enabled}});}} helpText="Preserves imported per-block designs. Product results still use app defaults when this is off. Hosted results pages always use the results page settings below." />
        <Select label="Appearance target" value={target} onChange={onTargetChange} options={[
            {label:'Shared appearance',value:'shared'},
            ...blockAppearanceCatalog.filter(c=>!vehicleBarInTheme || c.kind!=='vehicle_search_bar').map(c=>({label:`${c.label} — shared defaults`,value:`defaults:${c.kind}`})),
            ...config.profiles.filter(p=>!vehicleBarInTheme || p.kind!=='vehicle_search_bar').map(p=>({label:p.label,value:`${p.themeId}:${p.blockId}`}))
        ]}/>
        {editableComponent&&component&&<>
            <Text as="p" tone="subdued">{profile?'These overrides preserve this block’s design. Use default reconnects a setting to the shared appearance.':'These controls cover additional theme-block styles. Shared colour and layout controls are available in the Appearance panels below.'}</Text>
            {component.fields.filter(f=>profile||!f.uiKey).map(f=><BlockStack key={f.id} gap="100">
                {f.type==='checkbox'?<Checkbox label={f.label} checked={effective[f.id]===true} onChange={value=>setValue(f.id,value)}/>
                 :f.type==='select'?<Select label={f.label} options={f.options||[]} value={String(effective[f.id])} onChange={value=>setValue(f.id,value)}/>
                 :<TextField label={f.label} type={f.type==='range'?'number':'text'} value={String(effective[f.id])} min={f.min} max={f.max} step={f.step} autoComplete="off" onChange={value=>{if(f.type!=='range'||value!=='')setValue(f.id,f.type==='range'?Number(value):value);}} helpText={f.type==='color'?'Hex colour, e.g. #104190':undefined}/>}
                {Object.prototype.hasOwnProperty.call(overrides,f.id)&&<InlineStack><Button variant="plain" onClick={()=>setValue(f.id,undefined)}>Use default</Button></InlineStack>}
            </BlockStack>)}
            {kind==='fitment_table'&&<div aria-label="Fitment table appearance preview" style={{background:String(effective['gv-background']),color:String(effective['gv-text']),border:`1px solid ${effective['gv-border']}`,padding:effective['gv-compact']?8:16}}>
                <strong style={{fontSize:Number(effective['gv-heading-size'])}}>Vehicle fitment</strong>
                <div style={{background:String(effective['gv-selected-bg']),padding:8}}>2024 · Honda · CRF250R</div>
                <div style={{padding:8}}>2025 · Honda · CRF250R</div>
            </div>}
        </>}
    </BlockStack>;
}
